/**
 * Local, self-contained replacement for `@appointedd/zod-types`'s
 * `Document.BrandConfig` (schema/types/default constants) and
 * `Util.BrandConfig` (`resolveBrandPalette`/`resolveActiveTheme`).
 *
 * This mirrors that package's `Document/BrandConfig/index.ts` field-for-field
 * (same optionality, same default colour constants) — see
 * https://github.com/appointedd/zod-types (src/Document/BrandConfig/index.ts,
 * as of the version this repo last consumed).
 *
 * `services` no longer depends on `@appointedd/zod-types` directly: that
 * package's `mongodb` peer dependency was bumped to `>=5.5.0`, which
 * conflicts with this repo's pinned `mongodb@4.1.4` (an upgrade explicitly
 * deferred alongside separate planned work to extract BookingTool into its
 * own repo). Rather than depend on a package this repo can't actually
 * install cleanly, the small subset of zod-types this repo consumed
 * (BrandConfig/BrandPalette shapes, Appointedd default palettes, and the
 * `resolveBrandPalette`/`resolveActiveTheme` merge helpers) is copied here
 * as plain TypeScript — no runtime Zod validation is performed on these
 * types anywhere in this repo's actual business logic (see
 * `resolveEffectiveBrand.ts`/`theme.ts`/`OrganisationBrandConfig.ts`), so a
 * Zod schema isn't needed locally either.
 *
 * If this repo's `mongodb` version is upgraded in future (unblocking the
 * zod-types peer dependency), this file can be deleted and its consumers
 * pointed back at `@appointedd/zod-types` directly.
 */

/**
 * A single theme's worth of brand customisation (either "light" or "dark").
 * All fields are optional since an org/microsite/enterprise level may only
 * want to customise a subset of a theme's fields, falling back to
 * Appointedd static defaults (see `APPOINTEDD_DEFAULT_LIGHT_PALETTE`/
 * `APPOINTEDD_DEFAULT_DARK_PALETTE` below) for anything unset.
 */
export type BrandPalette = {
  primaryColor?: string;
  primaryActionColor?: string;
  accentColor?: string;
  // Derived shade helpers, kept for backward derivation compat within a
  // single palette. Consuming repos may compute these from primaryColor if
  // left unset.
  primaryDarkColor?: string;
  primaryLightColor?: string;
  surfaceColor?: string;
  textColor?: string;
  pageBackground?: string; // URL or CSS colour/gradient value
  // Per-theme overrides. If unset, falls back to the shared logoUrl/bannerUrl
  // on BrandConfig.
  logoUrl?: string;
  bannerUrl?: string;
};

/**
 * Canonical brand configuration shape, shared across MicrositeConfig,
 * OrganisationBrandConfig (services), and BookingTool.Brand (services).
 *
 * Supports side-by-side light AND dark theme customisation (rather than a
 * single active theme at a time), with automatic browser preference
 * detection ("auto" mode), manual override support, and Appointedd static
 * defaults as the ultimate fallback.
 */
export type BrandConfig = {
  // Org/microsite/enterprise-level customisations for each theme. Any field
  // left unset within a palette falls back to the corresponding Appointedd
  // default (see resolveBrandPalette).
  light?: BrandPalette;
  dark?: BrandPalette;
  // The default theme mode for this brand config. "auto" follows the
  // visitor's prefers-color-scheme system setting. "light"/"dark" force
  // that theme by default. This only controls the DEFAULT — it never traps
  // the visitor when allowVisitorThemeOverride is true.
  brandThemeMode?: "auto" | "light" | "dark";
  // Whether end visitors may manually toggle away from the org's default
  // theme mode. Defaults to true at consumption time (not a schema
  // default), so the org setting only controls the default, never the
  // visitor's ability to override it.
  allowVisitorThemeOverride?: boolean;
  // Shared across both themes — fonts aren't theme-specific.
  brandFontUrl?: string;
  brandFontFamily?: string;
  // Shared fallback logo/banner used when a theme-specific
  // light.logoUrl/dark.logoUrl (or bannerUrl) isn't set.
  logoUrl?: string;
  bannerUrl?: string;
  // Shared across both themes — favicon/title aren't theme-specific.
  faviconUrl?: string;
  pageTitleSuffix?: string;
};

/**
 * Appointedd static-default light palette, used as the fallback for any
 * light-theme field an org/microsite/enterprise hasn't customised.
 */
export const APPOINTEDD_DEFAULT_LIGHT_PALETTE: BrandPalette = {
  primaryColor: "#583976", // purple
  primaryActionColor: "#3c5b58", // teal
  accentColor: "#3c5b58", // teal
  surfaceColor: "#ffffff",
  textColor: "#1a1a1a", // matches the --ap-dark ink token
  pageBackground: "#ffffff",
};

/**
 * Appointedd static-default dark palette, used as the fallback for any
 * dark-theme field an org/microsite/enterprise hasn't customised.
 */
export const APPOINTEDD_DEFAULT_DARK_PALETTE: BrandPalette = {
  primaryColor: "#6b8fd9", // brightened purple variant
  accentColor: "#4fc9f5", // brightened teal/cyan variant
  surfaceColor: "#17151b", // near-black with slight purple undertone
  textColor: "#f3eee6", // warm off-white
  pageBackground: "#17151b",
};

/**
 * Full Appointedd static-default brand config, used as the ultimate
 * fallback when no org/microsite/enterprise level has customised anything.
 */
export const APPOINTEDD_DEFAULT_BRAND_CONFIG: BrandConfig = {
  light: APPOINTEDD_DEFAULT_LIGHT_PALETTE,
  dark: APPOINTEDD_DEFAULT_DARK_PALETTE,
  brandThemeMode: "auto",
  allowVisitorThemeOverride: true,
};

/**
 * Appointedd static-default favicon URL, used as the fallback for the
 * browser-tab icon when a org/microsite/enterprise level hasn't customised
 * `faviconUrl`.
 */
export const APPOINTEDD_DEFAULT_FAVICON_URL =
  "https://appointedd-assets.s3.eu-west-1.amazonaws.com/core/images/2025/Appointedd-logomark.svg";

export type { ResolvedTheme } from "./resolveBrandPalette";
export { resolveBrandPalette, resolveActiveTheme } from "./resolveBrandPalette";
